import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { activateCppRateCard } from '~/services/LuxLoyaltyService';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

interface ActivateCppRateCardParams {
  id: string;
  /**
   * When `false`, suppresses the "Rate card activated successfully" toast.
   * Defaults to showing the toast (`true`).
   */
  showSuccessToast?: boolean;
  /**
   * When `false`, suppresses the "Error activating rate card" toast.
   * Used by the rate card dialog, which surfaces mutation errors inline (MUI Alert)
   * instead of via snackbar. Defaults to showing the toast (`true`).
   */
  showErrorToast?: boolean;
}

/** Activates a DRAFT rate card and invalidates the list/detail caches. */
export function useActivateCppRateCard() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id }: ActivateCppRateCardParams) => {
      return await activateCppRateCard(id);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: costPerPointQueryKeys.all });
      // Both toast flags default to true (undefined → shown); normalise to a concrete boolean.
      if (variables?.showSuccessToast ?? true) {
        enqueueSnackbar('Rate card activated successfully', { variant: 'success' });
      }
    },
    onError: (error: Error, variables) => {
      if (variables?.showErrorToast ?? true) {
        enqueueSnackbar(`Error activating rate card: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });
}
